import { create } from "zustand";

import { DEFAULT_APP_PALETTE, isAppPalette, type AppPalette } from "@ceedero/ui";

/**
 * Which palette the interface is drawn in.
 *
 * "system" is a real third choice rather than the absence of one: it means
 * "follow this machine", and it keeps following it when the machine changes its
 * mind at sunset. Stored as the word the user chose, never as the light or dark
 * it resolved to on the day they chose it.
 */
export type ThemePreference = "dark" | "light" | "system";

export const THEME_STORAGE_KEY = "ceedero.theme";
export const PALETTE_STORAGE_KEY = "ceedero.palette";

const THEME_PREFERENCES = new Set<string>(["dark", "light", "system"]);

const isThemePreference = (value: unknown): value is ThemePreference =>
  typeof value === "string" && THEME_PREFERENCES.has(value);

/**
 * The stored preference, or the application's own default.
 *
 * Dark by default, which is the choice `index.html` also makes before any of
 * this runs — the two must agree or the first paint is one palette and the
 * second is another.
 */
const readStoredPreference = (): ThemePreference => {
  if (typeof localStorage === "undefined") {
    return "dark";
  }
  const stored: unknown = localStorage.getItem(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : "dark";
};

/**
 * The stored palette, or the package's default. A palette no stylesheet
 * defines — an older build's, a hand-edited value — reads as the default rather
 * than as a page drawn in nothing.
 */
const readStoredPalette = (): AppPalette => {
  if (typeof localStorage === "undefined") {
    return DEFAULT_APP_PALETTE;
  }
  const stored: unknown = localStorage.getItem(PALETTE_STORAGE_KEY);
  return isAppPalette(stored) ? stored : DEFAULT_APP_PALETTE;
};

const prefersDark = () =>
  typeof matchMedia === "undefined" ? true : matchMedia("(prefers-color-scheme: dark)").matches;

/** What a preference actually resolves to right now. */
export const resolveTheme = (preference: ThemePreference) => {
  if (preference !== "system") {
    return preference;
  }
  return prefersDark() ? "dark" : "light";
};

/**
 * The one place the palette reaches the document.
 *
 * The theme is a class on the root element because that is where the package's
 * dark palette is defined; setting it anywhere else would style a subtree and
 * leave the page behind it in the other palette.
 */
const applyTheme = (preference: ThemePreference) => {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.classList.toggle("dark", resolveTheme(preference) === "dark");
};

/**
 * The palette's tokens apply to the whole page through the attribute on the
 * root element; `@ceedero/ui`'s palette stylesheets answer it there, and on any
 * swatch that carries its own.
 */
const applyPalette = (palette: AppPalette) => {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.dataset.palette = palette;
};

interface ThemeState {
  preference: ThemePreference;
  palette: AppPalette;
  setPreference: (preference: ThemePreference) => void;
  setPalette: (palette: AppPalette) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  preference: readStoredPreference(),
  palette: readStoredPalette(),
  setPreference: (preference) => {
    set({ preference });
    applyTheme(preference);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  },
  setPalette: (palette) => {
    set({ palette });
    applyPalette(palette);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(PALETTE_STORAGE_KEY, palette);
    }
  },
}));

/**
 * Keeps "system" honest after the choice is made.
 *
 * Without this, following the machine means following it once — a machine that
 * switches to dark in the evening would leave this page light until it is
 * reloaded, which is the one thing choosing "system" was asking not to happen.
 */
export const watchSystemTheme = () => {
  if (typeof matchMedia === "undefined") {
    return () => {};
  }
  const query = matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (useThemeStore.getState().preference === "system") {
      applyTheme("system");
    }
  };
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
};
